// Aplicación de la marca de agua sobre un blob de imagen, client-side.
//
// Reusa el compositor de Edición Gráfica (compositeScene): se arma una escena
// mínima {imagen base + logo + texto} con el MISMO modelo en % que el editor,
// así el preview del editor y la descarga son idénticos pixel a pixel. Con la
// capa base id 'base', targetSize usa el tamaño natural de la imagen → la
// descarga no pierde resolución.

import { compositeScene } from '../pages/edicionGrafica/export/composite';
import { loadImageRatio } from '../pages/edicionGrafica/lib/factory';
import type { ImageLayer, Layer, Scene, TextLayer } from '../pages/edicionGrafica/scene/types';
import type { WatermarkConfig } from './watermarkConfig';

/** Capas de la marca de agua (sin la base). Pura: la geometría se testea sola. */
export function buildWatermarkLayers(
  config: WatermarkConfig,
  opts: { canvasRatio: number; logoSrc: string | null; logoNaturalRatio: number },
): Layer[] {
  const layers: Layer[] = [];
  if (config.logo.visible && opts.logoSrc) {
    const logo: ImageLayer = {
      id: 'wm-logo',
      type: 'image',
      src: opts.logoSrc,
      xPct: config.logo.xPct,
      yPct: config.logo.yPct,
      wPct: config.logo.wPct,
      // Igual que makeImage: el alto en % del lienzo se deriva del ratio natural
      // del logo para que NUNCA se deforme, sea cual sea el formato de la imagen.
      hPct: (config.logo.wPct * opts.canvasRatio) / (opts.logoNaturalRatio || 1),
      rotation: config.logo.rotation,
      opacity: config.logo.opacity,
      radiusPct: 0,
      naturalRatio: opts.logoNaturalRatio || 1,
    };
    layers.push(logo);
  }
  if (config.text.visible && config.text.text.trim()) {
    const text: TextLayer = {
      id: 'wm-text',
      type: 'text',
      text: config.text.text,
      xPct: config.text.xPct,
      yPct: config.text.yPct,
      wPct: config.text.wPct,
      hPct: config.text.fontSizePct * 1.3,
      rotation: config.text.rotation,
      opacity: config.text.opacity,
      fontFamily: config.text.fontFamily,
      fontSizePct: config.text.fontSizePct,
      fontWeight: config.text.fontWeight,
      fontStyle: config.text.fontStyle,
      color: config.text.color,
      align: 'center',
    };
    layers.push(text);
  }
  return layers;
}

/** JPEG se queda JPEG (calidad alta) para no inflar la descarga; el resto, PNG. */
export function pickOutputMime(inputMime: string): { mime: string; quality?: number } {
  if (inputMime === 'image/jpeg') return { mime: 'image/jpeg', quality: 0.92 };
  return { mime: 'image/png' };
}

// ── Tinte blanco (modo "Solo blanco") ────────────────────────────────────────
// source-in sobre el PNG transparente: pinta de blanco solo los píxeles con
// alpha. Cache por URL: una descarga masiva lo pide N veces.

const tintCache = new Map<string, Promise<string>>();

export function tintWhite(url: string): Promise<string> {
  const cached = tintCache.get(url);
  if (cached) return cached;
  const p = new Promise<string>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || 1;
        canvas.height = img.naturalHeight || 1;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('sin contexto 2D');
        ctx.drawImage(img, 0, 0);
        ctx.globalCompositeOperation = 'source-in';
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/png'));
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    };
    img.onerror = () => reject(new Error('No se pudo cargar el logo para teñirlo'));
    img.src = url;
  });
  tintCache.set(url, p);
  p.catch(() => tintCache.delete(url)); // no cachear fallos
  return p;
}

/** Resuelve la URL/dataURL del logo según el modo configurado. */
export async function resolveLogoSrc(
  config: WatermarkConfig,
  originalLogoUrl: string | null,
): Promise<string | null> {
  const { mode, noBgUrl } = config.logo;
  if (mode === 'no-bg') return noBgUrl ?? originalLogoUrl;
  if (mode === 'white') {
    // Sin la versión transparente no hay tinte posible (teñiría el rectángulo
    // entero); el editor procesa el fondo ANTES de permitir guardar este modo.
    return noBgUrl ? await tintWhite(noBgUrl) : originalLogoUrl;
  }
  return originalLogoUrl;
}

/**
 * Compone la marca de agua sobre el blob y devuelve el blob final. Lanza si no
 * puede componer (imagen corrupta, canvas tainted): el caller decide el fallback
 * (descargar sin marca) — acá no hay UI.
 */
export async function applyWatermarkToBlob(
  blob: Blob,
  config: WatermarkConfig,
  originalLogoUrl: string | null,
): Promise<Blob> {
  const src = URL.createObjectURL(blob);
  try {
    const ratio = await loadImageRatio(src);
    const logoSrc = await resolveLogoSrc(config, originalLogoUrl);
    const logoNaturalRatio = logoSrc ? await loadImageRatio(logoSrc) : 1;
    const base: ImageLayer = {
      // 'base' a propósito: targetSize la reconoce y exporta al tamaño natural.
      id: 'base',
      type: 'image',
      src,
      xPct: 50,
      yPct: 50,
      wPct: 100,
      hPct: 100,
      radiusPct: 0,
    };
    const scene: Scene = {
      bg: 'transparent',
      ratio,
      selectedId: null,
      layers: [base, ...buildWatermarkLayers(config, { canvasRatio: ratio, logoSrc, logoNaturalRatio })],
    };
    return await compositeScene(scene, pickOutputMime(blob.type));
  } finally {
    URL.revokeObjectURL(src);
  }
}
